import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  Card,
  CardMedia,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { apiService } from '../services/api';
import { SERVICE_CATEGORIES, getCategoryLabel } from '../config/categories';

const AddService = ({ onSuccess }) => {
  const [language, setLanguage] = useState('en');
  const [form, setForm] = useState({
    name: '',
    field: '',
    address: '',
    phone1: '',
    phone2: '',
    note: '',
    rate: '',
    rating: '4.5',
    photo: null,
  });
  const [preview, setPreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  const showAlert = (message, severity = 'success') => {
    setAlert({ message, severity });
    setTimeout(() => setAlert(null), 5000);
  };

  const validateFile = (file, maxSizeMB = 10) => {
    if (!file) {
      showAlert('❌ Please select an image', 'error');
      return false;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      showAlert(`❌ File size must be less than ${maxSizeMB}MB`, 'error');
      return false;
    }
    if (!file.type.startsWith('image/')) {
      showAlert('❌ Please select a valid image file', 'error');
      return false;
    }
    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!validateFile(file)) return;

    setForm((prev) => ({ ...prev, photo: file }));
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name || !form.field || !form.phone1 || !form.note || !form.address || !form.photo) {
      showAlert('❌ Please fill all required fields', 'error');
      return;
    }
  
    setLoading(true);
    try {
      const formData = new FormData();
      
      formData.append('name', form.name);
      formData.append('field', form.field);
      formData.append('address', form.address);
      formData.append('phone1', form.phone1);
      formData.append('phone2', form.phone2 || '');
      formData.append('note', form.note);
      formData.append('rate', form.rate || '');
      formData.append('email', '');
      formData.append('pending', 0);
      formData.append('status', 'active');
      formData.append('profileImage', form.photo);
  
      const response = await apiService.createServiceWithImage(formData);
      
      if (response.success) {
        showAlert('✅ Service added successfully!');
        setForm({
          name: '',
          field: '',
          address: '',
          phone1: '',
          phone2: '',
          note: '',
          rate: '',
          rating: '4.5',
          photo: null,
        });
        setPreview('');
        if (onSuccess) onSuccess();
      } else {
        showAlert('❌ Error: ' + (response.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      showAlert('❌ Error: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const labels = {
    en: {
      name: 'Service Provider Name',
      field: 'Service Field',
      address: 'Address/Location',
      note: 'Service Details/Note',
      phone1: 'Primary Phone',
      phone2: 'Secondary Phone (Optional)',
      rate: 'Rate/Price',
      rating: 'Rating',
      submit: '➕ Add Service',
      uploadPhoto: 'Click to upload service provider photo',
      contactInfo: 'Contact Information',
      selectService: 'Select a service type',
    },
    hi: {
      name: 'सेवा प्रदाता का नाम',
      field: 'सेवा क्षेत्र',
      address: 'पता/स्थान',
      note: 'सेवा विवरण/नोट',
      phone1: 'प्राथमिक फोन',
      phone2: 'द्वितीयक फोन (वैकल्पिक)',
      rate: 'दर/मूल्य',
      rating: 'रेटिंग',
      submit: '➕ सेवा जोड़ें',
      uploadPhoto: 'सेवा प्रदाता की फोटो अपलोड करने के लिए क्लिक करें',
      contactInfo: 'संपर्क जानकारी',
      selectService: 'एक सेवा प्रकार चुनें',
    },
  };

  const t = labels[language];

  return (
    <Box>
      {alert && (
        <Alert severity={alert.severity} onClose={() => setAlert(null)} sx={{ mb: 3 }}>
          {alert.message}
        </Alert>
      )}

      {/* ✅ LANGUAGE TOGGLE */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <ToggleButtonGroup
          value={language}
          exclusive
          onChange={(e, newLang) => newLang && setLanguage(newLang)}
          sx={{ bgcolor: '#f5f5f5' }}
        >
          <ToggleButton value="en" sx={{ fontWeight: language === 'en' ? 700 : 400 }}>
            🇬🇧 English
          </ToggleButton>
          <ToggleButton value="hi" sx={{ fontWeight: language === 'hi' ? 700 : 400 }}>
            🇮🇳 हिन्दी
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            
            <TextField 
              label={t.name} 
              name="name" 
              value={form.name} 
              onChange={handleChange} 
              fullWidth 
              required 
              placeholder={language === 'en' ? 'e.g., Aakash, Rajat Sharma' : 'उदाहरण: आकाश, राजत शर्मा'}
            />

            {/* ✅ FULL SERVICE LIST WITH ALL CATEGORIES */}
            <FormControl fullWidth required>
              <InputLabel>{t.field}</InputLabel>
              <Select 
                name="field" 
                value={form.field} 
                onChange={handleChange} 
                label={t.field}
              >
                <MenuItem value="" disabled>
                  <em>{t.selectService}</em>
                </MenuItem>
                {SERVICE_CATEGORIES.map((service) => (
                  <MenuItem key={service.value} value={service.value}>
                    {getCategoryLabel(service.value, 'service', language)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField 
              label={t.address} 
              name="address" 
              value={form.address} 
              onChange={handleChange} 
              fullWidth 
              required
              placeholder={language === 'en' ? 'e.g., Moh. Gadarpur, Bisauli' : 'उदाहरण: मोहल्ला गढ़पुर, बिसौली'}
              multiline
              rows={2}
            />

            <TextField 
              label={t.note} 
              name="note" 
              value={form.note} 
              onChange={handleChange} 
              fullWidth 
              required 
              multiline 
              rows={3}
              placeholder={language === 'en' ? 'e.g., chunai, plaster, farsh\n8 years of experience\nSpecializes in...' : 'उदाहरण: चूने का काम, प्लास्टर, फर्श\n8 वर्ष का अनुभव\nविशेषता...'}
            />

            <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#333' }}>
                📞 {t.contactInfo}
              </Typography>
              
              <TextField 
                label={t.phone1} 
                name="phone1" 
                value={form.phone1} 
                onChange={handleChange} 
                fullWidth 
                required
                type="tel"
                sx={{ mb: 1.5 }}
                placeholder={language === 'en' ? '9368556497' : '9368556497'}
              />

              <TextField 
                label={t.phone2} 
                name="phone2" 
                value={form.phone2} 
                onChange={handleChange} 
                fullWidth
                type="tel"
                placeholder={language === 'en' ? '9876543210' : '9876543210'}
              />
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField 
                  label={t.rate} 
                  name="rate" 
                  value={form.rate} 
                  onChange={handleChange} 
                  fullWidth 
                  placeholder={language === 'en' ? '₹500/hour' : '₹500/घंटा'}
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField 
                  label={t.rating} 
                  name="rating" 
                  type="number" 
                  value={form.rating} 
                  onChange={handleChange} 
                  fullWidth 
                  inputProps={{ min: 0, max: 5, step: 0.1 }} 
                />
              </Grid>
            </Grid>

            <Box sx={{ border: '2px dashed #f57c00', borderRadius: 2, p: 2, textAlign: 'center', cursor: 'pointer', transition: 'all 0.3s', '&:hover': { bgcolor: '#fff8f0' } }}>
              <input 
                type="file" 
                accept="image/*" 
                onChange={handleImageChange} 
                style={{ display: 'none' }} 
                id="service-image-input" 
              />
              <label htmlFor="service-image-input" style={{ cursor: 'pointer', display: 'block' }}>
                <CloudUploadIcon sx={{ fontSize: 40, color: '#f57c00', mb: 1 }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.uploadPhoto}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {form.photo ? form.photo.name : (language === 'en' ? 'PNG, JPG, GIF up to 10MB' : 'PNG, JPG, GIF 10MB तक')}
                </Typography>
              </label>
            </Box>

            <Button 
              type="submit" 
              variant="contained" 
              sx={{ bgcolor: '#f57c00', mt: 2, fontWeight: 700, py: 1.5 }} 
              disabled={loading}
              fullWidth
            >
              {loading ? <CircularProgress size={24} /> : t.submit}
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          {preview && (
            <Card sx={{ boxShadow: 3, borderRadius: 2, overflow: 'hidden' }}>
              <CardMedia 
                component="img" 
                height="250" 
                image={preview} 
                alt="Service preview" 
              />
              <Box sx={{ p: 2.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>{form.name || (language === 'en' ? 'Service Provider' : 'सेवा प्रदाता')}</Typography>
                <Typography variant="body2" sx={{ color: '#f57c00', fontWeight: 600, mb: 2 }}>
                  {getCategoryLabel(form.field, 'service', language) || (language === 'en' ? 'Select service type' : 'सेवा प्रकार चुनें')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, whiteSpace: 'pre-wrap', maxHeight: 100, overflow: 'auto', lineHeight: 1.6 }}>
                  {form.note || (language === 'en' ? 'Service details will appear here...' : 'सेवा विवरण यहां दिखाई देगा...')}
                </Typography>
                <Box sx={{ bgcolor: '#f9f9f9', p: 1.5, borderRadius: 1.5, border: '1px solid #e0e0e0' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.8, color: '#333' }}>
                    📍 {form.address || (language === 'en' ? 'Location' : 'स्थान')}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.8, color: '#333' }}>
                    📞 {form.phone1 ? `${form.phone1}${form.phone2 ? ` / ${form.phone2}` : ''}` : (language === 'en' ? 'Phone' : 'फोन')}
                  </Typography>
                  {form.rate && (
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#48bb78', display: 'block' }}>
                      💰 {form.rate}
                    </Typography>
                  )}
                  {form.rating && (
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#ffa500', display: 'block', mt: 0.5 }}>
                      ⭐ {form.rating}/5
                    </Typography>
                  )}
                </Box>
              </Box>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddService;